<template>
  <div>
    <h1 class="display-1 font-weight-black primary--text mb-2">
      Primeiro acesso
    </h1>
    <p class="subheading">Insira seus dados abaixo para acessar o portal.</p>
    <v-form
      ref="form"
      lazy-validation
      v-model="valido"
      @keyup.native.enter="valido && enviar()"
    >
      <v-select
        required
        item-text="nome"
        item-value="id"
        label="Unimed"
        name="unimed"
        v-model="unimed"
        :items="unimeds"
        :rules="unimedRules"
        @change="getUrlPrimeiroAcesso"
      ></v-select>
      <div v-if="!urlPrimeiroAcesso">
        <v-text-field
          required
          data-cy="nome"
          label="Nome"
          name="nome"
          type="text"
          v-model="nome"
          :rules="nomeRules"
        ></v-text-field>
        <v-text-field
          required
          data-cy="numeroCartao"
          label="Número do cartão"
          name="numeroCartao"
          type="text"
          v-mask="'# ### ############ #'"
          v-model="numeroCartao"
          :rules="numeroCartaoRules"
          :masked="false"
        ></v-text-field>
        <v-text-field
          required
          data-cy="dataNascimento"
          label="Data de Nascimento"
          type="text"
          name="dataNascimento"
          v-model="dataNascimento"
          v-mask="'##/##/####'"
          :rules="dataNascimentoRules"
          :masked="true"
        ></v-text-field>
        <v-text-field
          persistent-hint
          required
          data-cy="cpf"
          hint="Para dependentes sem CPF, favor informar o CPF do Titular. Para titulares menores de idade ou incapaz, favor informar o CPF do responsavel legal"
          label="CPF"
          type="text"
          name="cpf"
          v-mask="'###.###.###-##'"
          v-model="cpf"
          :masked="false"
          :rules="cpfRules"
        ></v-text-field>
        <v-checkbox
          color="primary"
          label="Beneficiário Titular Incapaz"
          v-model="beneficiarioIncapaz"
        />
        <v-text-field
          required
          data-cy="email"
          label="E-mail"
          type="email"
          name="email"
          v-model="email"
          :rules="emailRules"
        ></v-text-field>
        <v-text-field
          required
          data-cy="password"
          label="Senha"
          name="password"
          type="password"
          v-model="password"
          :rules="passwordRules"
        ></v-text-field>
        <v-text-field
          required
          data-cy="passwordConfirmar"
          label="Confirmação de Senha"
          name="passwordConfirmar"
          type="password"
          v-model="passwordConfirmar"
          :rules="passwordConfirmarRules"
        ></v-text-field>
      </div>
      <div v-else>
        <v-btn
          light
          data-cy="joinSubmitBtn"
          class="mr-0"
          :color="highContrast ? 'white' : 'primary'"
          :href="urlPrimeiroAcesso"
          >Acessar</v-btn
        >
      </div>
    </v-form>
    <v-spacer></v-spacer>
    <v-layout align-center column v-if="!urlPrimeiroAcesso">
      <vue-recaptcha
        sitekey="6LeVBiopAAAAAI8KsTFl4-Ee7Hsyw6_XP5E7Enua"
        :loadRecaptchaScript="true"
        @verify="marcarRecaptchaComoVerificado"
        @expired="resetRecaptcha"
      ></vue-recaptcha>
      <div>
        <small class="caption red--text">
          {{ recaptchaMensagem }}
        </small>
      </div>
    </v-layout>
    <v-layout
      align-end
      justify-space-between
      fill-height
      row
      v-if="!urlPrimeiroAcesso"
    >
      <v-btn
        flat
        color="grey darken-2"
        class="body-2 text-none mx-0 px-1"
        :to="{ name: 'recuperar-senha' }"
        >Esqueci a senha</v-btn
      >
      <v-btn
        light
        class="mr-0"
        data-cy="joinSubmitBtn"
        :color="highContrast ? 'white' : 'primary'"
        :disabled="!valido"
        @click="enviar"
        >Acessar</v-btn
      >
    </v-layout>
    <p class="mt-4 subheading">
      Já possui acesso?
      <v-btn
        flat
        color="grey darken-1"
        class="body-2 text-none mx-0 px-1"
        :to="{ name: 'acessar' }"
        >Faça o acesso aqui</v-btn
      >
    </p>
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import VueRecaptcha from "vue-recaptcha";
import { mask } from "vue-the-mask";

import { passwordValidation } from "@/utils/validation-rules";

import { storage } from "@/plugins";

import { verificaRepasse } from "@/services/beneficiario";
import { getUrlPrimeiroAcesso } from "@/services/usuario";

import { formataData, valorNumerico } from "@/utils/formataValores";
import moment from "moment";

const { mapMutations } = createNamespacedHelpers("portal");

export default {
  name: "PrimeiroAcesso",
  props: {
    highContrast: Boolean,
    unimeds: Array
  },
  directives: { mask },
  components: {
    VueRecaptcha
  },
  data() {
    return {
      numeroCartao: null,
      cartaoTitular: null,
      cpf: null,
      dataNascimento: null,
      email: null,
      emailConfirmacao: null,
      nome: null,
      password: null,
      passwordAcessar: null,
      passwordConfirmar: null,
      recaptchaMensagem: null,
      recaptchaToken: null,
      recaptchaVerificado: false,
      titular: true,
      unimed: null,
      urlPrimeiroAcesso: null,
      beneficiarioIncapaz: false,
      valido: false,
      numeroCartaoRules: [
        value => !!value || "O Número do cartão é obrigatório"
      ],
      cartaoTitularRules: [
        value => !!value || "O Número do cartão do titular é obrigatório"
      ],
      cpfRules: [
        value => !!value || "O CPF é obrigatório",
        value => (value && value.length === 14) || "O CPF deve ser válido"
      ],
      dataNascimentoRules: [
        value => !!value || "A Data de nascimento é obrigatória",
        value =>
          this.isValidDate(value) || "A Data de nascimento deve ser válida"
      ],
      emailRules: [
        value => !!value || "O  E-mail é obrigatório",
        value => /.+@.+/.test(value) || "O E-mail deve ser válido"
      ],
      nomeRules: [
        value => !!value || "O Nome é obrigatório",
        value =>
          (value && value.length > 2 && value.length <= 200) ||
          "O Nome deve entre 3 e 200 caracteres"
      ],
      passwordRules: [
        value => !!value || "A senha é obrigatória",
        value => passwordValidation(value)
      ],
      passwordAcessarRules: [value => !!value || "A senha é obrigatória"],
      unimedRules: [value => !!value || "É obrigatório selecionar uma Unimed"]
    };
  },
  computed: {
    passwordConfirmarRules() {
      return [
        value => !!value || "A confirmação de senha é obrigatória",
        value =>
          (value && value.length >= 8) ||
          "A senha deve ser maior do que 8 caracteres",
        () =>
          this.password === this.passwordConfirmar || "A senha deve coincidir"
      ];
    }
  },
  created() {
    this.unimedSelecionada();
    this.getUrlPrimeiroAcesso(this.unimed);
  },
  methods: {
    ...mapMutations(["setShowLoading", "setToast"]),
    blurCartaoBeneficiario() {
      this.verificarRepasse();
    },
    isValidDate(value) {
      var data = moment(value, "DD/MM/YYYY");
      return data.isValid();
    },
    enviar() {
      if (!this.recaptchaVerificado) {
        this.recaptchaMensagem = "Confirme o reCAPTCHA";
      } else if (this.$refs.form.validate()) {
        const params = {
          nome: this.nome,
          email: this.email,
          emailConfirmation: this.emailConfirmacao,
          senha: this.password,
          codigoBeneficiario: valorNumerico(this.numeroCartao),
          codigoTitular: valorNumerico(this.cartaoTitular),
          dataNascimento: formataData(this.dataNascimento),
          cpf: valorNumerico(this.cpf),
          incapaz: this.beneficiarioIncapaz,
          agreement: true,
          unimed: this.unimed,
          recaptcha: this.recaptchaToken
        };

        this.$emit("primeiro-acesso", params);
      }
    },
    getUrlPrimeiroAcesso(unimedId) {
      if (unimedId) {
        this.setShowLoading(true);

        getUrlPrimeiroAcesso(unimedId)
          .then(response => {
            this.urlPrimeiroAcesso = response;
          })
          .catch(() => {
            this.urlPrimeiroAcesso = "";
          })
          .finally(() => {
            this.setShowLoading(false);
          });
      }
    },
    marcarRecaptchaComoVerificado(token) {
      this.recaptchaMensagem = null;
      this.recaptchaVerificado = true;
      this.recaptchaToken = token;
    },
    resetRecaptcha() {
      this.recaptchaVerificado = false;
      this.recaptchaToken = null;
    },
    unimedSelecionada() {
      const unimed = storage.getItem("unimed");
      const unimedSelecionada = unimed ? Number(unimed) : null;

      this.unimed = unimedSelecionada;
    },
    verificarRepasse() {
      if (this.numeroCartao && this.unimed) {
        const numeroCartaoNumerico = valorNumerico(this.numeroCartao);

        this.setShowLoading(true);

        verificaRepasse(numeroCartaoNumerico, this.unimed)
          .then(response => {
            if (response) {
              this.setToast(
                "Os dados informados são de um contrato em Repasse, para realizar consultas e movimentações, será necessário criar um acesso no portal da sua Unimed de origem"
              );
            }
            this.unimed = Number(response.unimedOrigem);
          })
          .catch(({ response }) => {
            this.setToast(response.data.message);
          })
          .finally(() => {
            this.setShowLoading(false);
          });
      }
    }
  }
};
</script>
